import { useState, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Box, H5, Snackbar } from '@parspec/pixel';

import { saveColumnOrderEvents, useGetBomTabStatsQuery } from '../queries';
import { getStats } from './helper';
import Footer from '../Footer';
import BomTabHeader from './BomTabHeader';
import BomTable from './BomTable';
import { useCustomisationColumn, useGetCustomisedColumnData, useProjectBomsMappedData } from '../shared/hooks';
import LinkedBomsPopup from '../shared/LinkedBomsPopup';
import { ManageColumnsPopup } from 'src/features/Dashboard/shared/components/ManageColumnsPopup/ManageColumnsPopup';
import { COLUMN_OPTIONS_BOM_TAB } from '../pricing&LeadTime/shared/constants';
import { useGetUserProfileInfoQuery } from 'src/features/Settings/MyProfile/queries';
import { useGetSelectedColumnsAndPersistingFiltersQuery } from 'src/features/shared/queries';
import ProductOrOptionTableContent from '../shared/ProductOrOptionTableContent';
import { ProcessedItem } from '../shared/types';
import { getApiDataForPricingInfo } from '../pricing&LeadTime/shared/utils';
import { MainProductDataType } from '../pricing&LeadTime/mainProductTable';
import { usePricingBodPatchMutation } from '../pricing&LeadTime/queries';
import { useBomAllTableData } from '../shared/hooks/useBomAllTableData';
import { EVENT_SERVICE_PAGENAME_VALUES } from 'src/features/shared/utils/constants';


const BillOfMaterialsTable = () => {
    const { bomId = '', projectId = 0 } = useParams();

    const [openManageColumnsPopup, setOpenManageColumnsPopup] = useState<boolean>(false);
    const [tableMaxHeight, setTableMaxHeight] = useState(300);
    const selectedRowsRef = useRef<Set<number>>(new Set());
    const currentGroupWhilePastingRef = useRef<number | null>(null);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const { handleTablesReordering, handleTablesWidthChanged } = useCustomisationColumn();
    const { data: userData } = useGetUserProfileInfoQuery();
    const { columnOrderDetails, isColumnDataLoading } = useGetCustomisedColumnData(bomId, EVENT_SERVICE_PAGENAME_VALUES.BOM_SPECIFICATIONS);
    const { isFetching: isSelectedColumnsAndFiltersQueryDataFetching } = useGetSelectedColumnsAndPersistingFiltersQuery(
        { userId: userData?.data?.id || 0, pageName: EVENT_SERVICE_PAGENAME_VALUES.BOM_SPECIFICATIONS },
        { enabled: true }
    );
    const { data: bomTabStats } = useGetBomTabStatsQuery(bomId!, {
        enabled: Boolean(bomId),
    });

    const { projectBomsAndPrimaryData } = useProjectBomsMappedData(Number(projectId));
    const { projectBomsMappedData, primaryBomId } = projectBomsAndPrimaryData;
    const statsData = getStats(bomTabStats);

    /** A common hook that prepares table data for all the tabs */
    const { section, option, isDataLoading, mainProductOptionsInfo, optionsApiData, currentTabValue } = useBomAllTableData(bomId);


    const { mutateAsync: updatePricingData } = usePricingBodPatchMutation();

    const handlePricingInfoEdit = useCallback(
        async function (rowData: Partial<Omit<MainProductDataType, 'qty' | 'type' | 'manufacturer' | 'modelNo' | 'description' | 'specification' | 'lotInfo'>>) {
            const { id: bodId = 0, ...restRowInfo } = rowData;
            const apiReqData = getApiDataForPricingInfo(restRowInfo);
            if (bodId && apiReqData) {
                await updatePricingData({ bomId: Number(bomId), pricingBod: apiReqData, bodId: bodId, shouldCancelQueries: false });
            }
        },
        [updatePricingData, bomId]
    );

    const onColumnSelection = async (rowData: string[]) => {
        await saveColumnOrderEvents(EVENT_SERVICE_PAGENAME_VALUES.BOM_SPECIFICATIONS, rowData);
        setOpenManageColumnsPopup(() => false);
    };

    const isBomLinked = projectBomsMappedData.get(Number(bomId));

    function sectionRefCallback(ref: HTMLDivElement) {
        if (ref) {
            setTableMaxHeight(ref.offsetHeight - 105);
        }
    }

    const getSectionBodItems = useCallback((sectionId: number, isOptionTable?: boolean) => {
        const data = isOptionTable ? option : section;
        const key = isOptionTable ? 'substitute_section' : "section";
        const sectionItems = (data || []).filter((item: ProcessedItem) => item[key as keyof typeof item] === sectionId);
        return sectionItems || []
    }, [option, section])

    const accOptions = useMemo(() => {
        return [
            {
                labelId: 'Products',
                summary: <Box pl={2} position="sticky" top="0" zIndex="3" >
                    <H5>Products</H5>
                </Box>,
                details:
                    <Box mt={2}>
                        <BomTable
                            dataLoading={isDataLoading}
                            tableData={section || [] as never}
                            dataFetching={isDataLoading}
                            mainProductOptionsInfo={mainProductOptionsInfo}
                            handleTablesReordering={handleTablesReordering}
                            handleTablesWidthChanged={handleTablesWidthChanged}
                            selectedRowsRef={selectedRowsRef}
                            tableMaxHeight={tableMaxHeight}
                            optionSection={optionsApiData?.data || []}
                            getSectionBodItems={getSectionBodItems}
                            currentGroupWhilePastingRef={currentGroupWhilePastingRef}
                            onPricingInfoEdit={handlePricingInfoEdit}
                            currentTabValue={currentTabValue}
                        />
                    </Box>,
                rightSummary: <BomTabHeader setOpenManageColumnsPopup={setOpenManageColumnsPopup} hasOption={option?.length > 0} />
            },
            {
                labelId: 'Options',
                summary: <Box pl={2} position="sticky" top="0" zIndex="3" >
                    <H5>Options</H5>
                </Box>,
                details:
                    <Box mt={2}>
                        <BomTable
                            dataLoading={isDataLoading}
                            tableData={option || [] as never}
                            dataFetching={isDataLoading}
                            mainProductOptionsInfo={mainProductOptionsInfo}
                            isOptionTable
                            handleTablesReordering={handleTablesReordering}
                            handleTablesWidthChanged={handleTablesWidthChanged}
                            selectedRowsRef={selectedRowsRef}
                            tableMaxHeight={tableMaxHeight}
                            optionSection={optionsApiData?.data || []}
                            currentGroupWhilePastingRef={currentGroupWhilePastingRef}
                            onPricingInfoEdit={handlePricingInfoEdit}
                            getSectionBodItems={getSectionBodItems}
                            currentTabValue={currentTabValue}
                        />
                    </Box>
            }

        ]
    }, [isDataLoading, section, mainProductOptionsInfo, handleTablesReordering, handleTablesWidthChanged, tableMaxHeight, optionsApiData?.data, getSectionBodItems, handlePricingInfoEdit, option])

    return (
        <>
            {isBomLinked ? (
                <Box flex={1} mx={4} display="flex" flexDirection="column" minHeight={0} border='1px solid' borderColor='neutral.main' borderRadius={2} sx={{ borderTopLeftRadius: 0 }} bgcolor='white'>
                    <LinkedBomsPopup primaryBomId={primaryBomId || 0} isbomLoading={isDataLoading} />
                </Box>
            ) : (
                <>
                    <Box flex={1} mx={4} display="flex" flexDirection="column" minHeight={0} border='1px solid' borderColor='neutral.main' borderRadius={2} sx={{ borderTopLeftRadius: 0 }}>
                        <Snackbar open={!!snackbarMessage} onClose={() => setSnackbarMessage('')} message={snackbarMessage}></Snackbar>
                        <Box flex={1} minHeight={0} display="flex" ref={sectionRefCallback} flexDirection="column" overflow='auto'>
                            <ProductOrOptionTableContent bomId={bomId} options={accOptions} />
                        </Box>
                        {openManageColumnsPopup && (
                            <ManageColumnsPopup
                                isOpen={openManageColumnsPopup}
                                setIsOpen={setOpenManageColumnsPopup}
                                allColumnOptions={[...COLUMN_OPTIONS_BOM_TAB.data]}
                                setSnackbarData={setSnackbarMessage}
                                onColumnSelection={onColumnSelection}
                                isSelectedColumnsMutationLoading={isColumnDataLoading}
                                selectedColumnsData={columnOrderDetails && columnOrderDetails?.order}
                                isSelectedColumnsQueryDataFetching={isSelectedColumnsAndFiltersQueryDataFetching}
                                tab={true}
                            />
                        )}
                    </Box>
                    <Footer statsData={statsData} minCardWidth={100} />
                </>
            )}
        </>
    );
};

export default BillOfMaterialsTable;
